import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronRight, List, X } from 'lucide-react';
import { CourseLesson, LessonMenu, canOpenLesson } from '@learninglinq/course-engine';
import '@learninglinq/course-engine/styles.css';
import { api, getUser } from '../lib/api';

const FLUSH_MS = 5000;
const MAX_BATCH = 100;

// Telemetry is queued in localStorage so events survive a closed tab or a
// dropped connection; the server ignores repeats by client_event_id.
function useEventQueue(enrollmentId) {
  const key = `ce_events_${enrollmentId}`;
  const queue = useRef([]);
  const sending = useRef(false);

  const persist = useCallback(() => {
    try { localStorage.setItem(key, JSON.stringify(queue.current)); } catch { /* private mode */ }
  }, [key]);

  useEffect(() => {
    try { queue.current = JSON.parse(localStorage.getItem(key) || '[]'); } catch { queue.current = []; }
  }, [key]);

  const flush = useCallback(async ({ keepalive = false } = {}) => {
    if (sending.current || !queue.current.length) return;
    sending.current = true;
    const batch = queue.current.slice(0, MAX_BATCH);
    try {
      const res = await fetch(`/api/learning/engine/${enrollmentId}/events`, {
        method: 'POST',
        credentials: 'include',
        keepalive,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: batch }),
      });
      // 4xx means the batch itself is unusable; drop it rather than retry forever.
      if (res.ok || (res.status >= 400 && res.status < 500 && res.status !== 401 && res.status !== 429)) {
        queue.current = queue.current.slice(batch.length);
        persist();
      }
    } catch { /* offline; retry on the next tick */ }
    sending.current = false;
  }, [enrollmentId, persist]);

  const push = useCallback((event) => {
    queue.current.push(event);
    persist();
    if (queue.current.length >= 20) flush();
  }, [flush, persist]);

  useEffect(() => {
    const t = setInterval(flush, FLUSH_MS);
    const onHide = () => flush({ keepalive: true });
    window.addEventListener('pagehide', onHide);
    return () => {
      clearInterval(t);
      window.removeEventListener('pagehide', onHide);
      flush({ keepalive: true });
    };
  }, [flush]);

  return push;
}

export default function EngineCoursePlayer({ enrollmentId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lessonId, setLessonId] = useState(null);
  const [progress, setProgress] = useState({});
  const [status, setStatus] = useState(null);
  const pushEvent = useEventQueue(enrollmentId);
  const saveTimers = useRef({});
  const pendingSaves = useRef({});
  // Each quiz's reset generation as loaded, sent with every save so the server
  // can tell a save from before an admin reset of its attempts.
  const quizResets = useRef({});

  useEffect(() => {
    let cancelled = false;
    api(`/api/learning/engine/${enrollmentId}`)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        quizResets.current = Object.fromEntries(
          Object.entries(d.progress || {}).map(([lid, lp]) => [
            lid,
            Object.fromEntries(Object.entries(lp?.quizzes || {}).map(([bid, q]) => [bid, q?.reset?.generation ?? 0])),
          ]),
        );
        setProgress(d.progress || {});
        setStatus(d.status);
        const lessons = d.version.content.lessons;
        const next = lessons.find((l) => !d.progress?.[l.id]?.lessonCompleted) || lessons[0];
        setLessonId(next.id);
      })
      .catch((err) => !cancelled && setError(err.body?.error === 'no_content' ? 'This course has no content yet.' : err.message));
    return () => { cancelled = true; };
  }, [enrollmentId]);

  const saveProgress = useCallback(async (id, p, { keepalive = false } = {}) => {
    delete pendingSaves.current[id];
    try {
      const res = await fetch(`/api/learning/engine/${enrollmentId}/progress`, {
        method: 'POST',
        credentials: 'include',
        keepalive,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lesson_id: id, progress: p, quiz_resets: quizResets.current[id] || {} }),
      });
      if (res.ok) setStatus((await res.json()).status);
    } catch (err) {
      console.error('Progress save failed:', err);
    }
  }, [enrollmentId]);

  // Saves are debounced per lesson; completion saves go out immediately.
  const onProgress = useCallback((id, p) => {
    setProgress((prev) => ({ ...prev, [id]: p }));
    clearTimeout(saveTimers.current[id]);
    pendingSaves.current[id] = p;
    if (p.lessonCompleted) saveProgress(id, p);
    else saveTimers.current[id] = setTimeout(() => saveProgress(id, p), 1500);
  }, [saveProgress]);

  useEffect(() => {
    const flushSaves = () => {
      for (const [id, p] of Object.entries(pendingSaves.current)) saveProgress(id, p, { keepalive: true });
    };
    window.addEventListener('pagehide', flushSaves);
    return () => {
      window.removeEventListener('pagehide', flushSaves);
      flushSaves();
    };
  }, [saveProgress]);

  const content = data?.version.content;
  const lessons = content?.lessons || [];
  const index = lessons.findIndex((l) => l.id === lessonId);
  const lesson = lessons[index];
  const nextLesson = lessons[index + 1];

  const resolveUrl = useMemo(() => {
    const base = data?.media_base;
    return (src) => (base && (src.startsWith('/') || src.startsWith('./')) && !src.startsWith('//') ? `${base}/${src.replace(/^\.?\//, '')}` : src);
  }, [data?.media_base]);

  const menuRef = useRef(null);
  // The lesson menu won't offer a locked lesson; the server would refuse its progress anyway.
  const openLesson = (id) => {
    if (!canOpenLesson(lessons, progress, content?.navigation, id)) return;
    setLessonId(id);
    menuRef.current?.close();
    window.scrollTo({ top: 0 });
  };
  const menu = (
    <LessonMenu lessons={lessons} progress={progress} navigation={content?.navigation} current={lessonId} onOpen={openLesson} brand="learnable" />
  );

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-xl font-semibold text-red-600">Can't open course</h1>
        <p className="mt-2 text-slate-600">{error}</p>
        <button onClick={() => navigate('/dashboard')} className="mt-4 text-brand-dark underline">Back to dashboard</button>
      </div>
    );
  }
  if (!data || !lesson) return <div className="max-w-2xl mx-auto px-4 py-12 text-slate-500">Loading course…</div>;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 py-2 backdrop-blur">
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">{data.course.title}</span>
        {lessons.length > 1 && (
          <button
            type="button"
            onClick={() => menuRef.current?.showModal()}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1 text-sm text-slate-700 hover:bg-slate-50 lg:hidden"
          >
            <List size={16} aria-hidden="true" /> Lessons
          </button>
        )}
        <Link to="/dashboard" className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900">
          <X size={16} /> Exit course
        </Link>
      </header>

      {/* Phones and tablets: the same menu in a drawer from the left. */}
      {lessons.length > 1 && (
        <dialog
          ref={menuRef}
          aria-label="Lessons"
          className="m-0 h-full max-h-full w-80 max-w-[85vw] overflow-y-auto bg-white p-4 shadow-xl backdrop:bg-slate-900/40 lg:hidden"
          onClick={(e) => e.target === e.currentTarget && menuRef.current?.close()}
        >
          <div className="mb-2 flex justify-end">
            <button type="button" onClick={() => menuRef.current?.close()} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-slate-600 hover:bg-slate-100">
              <X size={16} aria-hidden="true" /> Close
            </button>
          </div>
          {menu}
        </dialog>
      )}

      {status === 'completed' && (
        <div className="border-b border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" role="status">
          <span className="inline-flex items-center gap-1.5 font-semibold"><CheckCircle size={16} /> Course complete.</span>{' '}
          Your certificate is on your <Link to="/dashboard" className="underline">dashboard</Link>.
        </div>
      )}

      <div className="mx-auto flex max-w-7xl">
        {lessons.length > 1 && (
          <aside className="sticky top-12 hidden h-[calc(100vh-3rem)] w-72 shrink-0 overflow-y-auto border-r border-slate-200 p-4 lg:block">
            {menu}
          </aside>
        )}

        <main className="min-w-0 flex-1">
          <CourseLesson
            key={lesson.id}
            lesson={lesson}
            courseId={content.id}
            courseVersion={data.version.number}
            learnerId={getUser()?.id ?? null}
            lang={content.lang}
            brand="learnable"
            resolveUrl={resolveUrl}
            initialProgress={progress[lesson.id]}
            onProgress={(p) => onProgress(lesson.id, p)}
            onEvent={pushEvent}
          />
          {nextLesson && progress[lesson.id]?.lessonCompleted && (
            <div className="mx-auto max-w-[46rem] px-4 pb-16">
              <button
                type="button"
                onClick={() => openLesson(nextLesson.id)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-5 py-3 font-semibold text-slate-900 hover:bg-brand-dark"
              >
                Next: {nextLesson.title} <ChevronRight size={18} />
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
